import { useEffect, useMemo, useState } from "react";
import {
  aggregationFns,
  columnFilteringFeature,
  columnGroupingFeature,
  columnOrderingFeature,
  columnPinningFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createCoreRowModel,
  createExpandedRowModel,
  createFilteredRowModel,
  createGroupedRowModel,
  createSortedRowModel,
  createTableHook,
  filterFns,
  globalFilteringFeature,
  rowExpandingFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFns,
  tableFeatures,
  type CellData,
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ColumnPinningState,
  type ColumnSizingState,
  type ColumnVisibilityState,
  type ExpandedState,
  type GroupingState,
  type OnChangeFn,
  type RowData,
  type RowSelectionState,
  type SortingState,
  type TableFeatures,
  type TableOptions,
} from "@tanstack/react-table";
import { dynamicFilterFn, fuzzyFilter } from "./filters";
import type { NameSortMode } from "./nameSorting";
import { footerRowCountFeature } from "./footerRowCountFeature";

export const appTableFeatures = tableFeatures({
  rowSortingFeature,
  rowSelectionFeature,
  rowExpandingFeature,
  columnFilteringFeature,
  columnGroupingFeature,
  columnOrderingFeature,
  columnVisibilityFeature,
  columnSizingFeature,
  columnResizingFeature,
  columnPinningFeature,
  globalFilteringFeature,
  footerRowCountFeature,
});

export type AppTableFeatures = typeof appTableFeatures;
export type AppColumnDef<TData extends RowData, TValue extends CellData = CellData> = ColumnDef<AppTableFeatures, TData, TValue>;

declare module "@tanstack/react-table" {
  interface ColumnMeta<
    // Generic parameters are required to match TanStack's declaration for module augmentation.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    TFeatures extends TableFeatures,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    TData extends RowData,
    TValue extends CellData = CellData,
  > {
    label?: string;
    headerTooltip?: React.ReactNode;
    compact?: boolean;
    lockWidth?: boolean;
    variant?: "text" | "number" | "date" | "boolean" | "select" | "multi-select";
    options?: Array<{ label: string; value: string; count?: number }>;
    align?: "left" | "center" | "right";
    truncate?: boolean;
    hideHeaderControl?: boolean;
    selection?: boolean;
    tree?: boolean;
    treeCell?: (row: TData) => React.ReactNode;
    nameSorting?: {
      mode: NameSortMode;
      onModeChange: (mode: NameSortMode) => void;
    };
    conditionalFormatting?: {
      style?: (value: TValue | unknown) => React.CSSProperties | undefined;
    };
  }
}

const {
  createAppColumnHelper,
  useAppTable: useBaseAppTable,
} = createTableHook({
  _features: appTableFeatures,
  _rowModels: {
    coreRowModel: createCoreRowModel(),
    filteredRowModel: createFilteredRowModel({
      ...filterFns,
      fuzzy: fuzzyFilter,
      dynamic: dynamicFilterFn,
    }),
    groupedRowModel: createGroupedRowModel(aggregationFns),
    sortedRowModel: createSortedRowModel(sortFns),
    expandedRowModel: createExpandedRowModel(),
  },
  defaultColumn: {
    minSize: 56,
    maxSize: 900,
    filterFn: dynamicFilterFn,
  },
  globalFilterFn: "fuzzy",
  columnResizeMode: "onChange",
});

export { createAppColumnHelper };

type PersistedTableState = {
  columnFilters?: ColumnFiltersState;
  columnOrder?: ColumnOrderState;
  columnPinning?: ColumnPinningState;
  columnSizing?: ColumnSizingState;
  columnVisibility?: ColumnVisibilityState;
  globalFilter?: string;
  grouping?: GroupingState;
  rowSelection?: RowSelectionState;
  sorting?: SortingState;
};

type AppTableOptions<TData extends RowData> = Omit<
  TableOptions<AppTableFeatures, TData>,
  "_features" | "_rowModels"
> & {
  tableKey: string;
  persistState?: boolean;
  /** Use TanStack's native store for server-driven tables without controlled/persisted state. */
  nativeState?: boolean;
};

function storageKey(tableKey: string) {
  return `portal:data-table:${tableKey}:v1`;
}

function readPersistedState(tableKey: string): PersistedTableState {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(storageKey(tableKey));
    return raw ? JSON.parse(raw) as PersistedTableState : {};
  } catch {
    return {};
  }
}

function applyUpdater<T>(updater: T | ((old: T) => T), current: T) {
  return typeof updater === "function" ? (updater as (old: T) => T)(current) : updater;
}

function sameState<T>(left: T, right: T) {
  if (Object.is(left, right)) return true;
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

function chainOnChange<T>(localSet: React.Dispatch<React.SetStateAction<T>>, external?: OnChangeFn<T>): OnChangeFn<T> {
  return (updater) => {
    // Some table features initialise themselves while the table is being read for
    // the first render. Deferring the state write prevents React's "setState in
    // render" warning and lets the feature settle on the next microtask.
    queueMicrotask(() => {
      localSet((current) => {
        const next = applyUpdater(updater, current);
        if (sameState(next, current)) return current;
        external?.(next);
        return next;
      });
    });
  };
}

export function useAppTable<TData extends RowData>({
  tableKey,
  persistState = true,
  nativeState = false,
  state,
  initialState,
  onColumnOrderChange,
  onColumnFiltersChange,
  onColumnPinningChange,
  onColumnSizingChange,
  onColumnVisibilityChange,
  onExpandedChange,
  onGlobalFilterChange,
  onGroupingChange,
  onRowSelectionChange,
  onSortingChange,
  ...options
}: AppTableOptions<TData>) {
  const persisted = useMemo(() => (persistState ? readPersistedState(tableKey) : {}), [persistState, tableKey]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(state?.columnFilters ?? persisted.columnFilters ?? initialState?.columnFilters ?? []);
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(state?.columnOrder ?? persisted.columnOrder ?? initialState?.columnOrder ?? []);
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(state?.columnPinning ?? persisted.columnPinning ?? initialState?.columnPinning ?? { left: [], right: [] });
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(state?.columnSizing ?? persisted.columnSizing ?? initialState?.columnSizing ?? {});
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>(state?.columnVisibility ?? persisted.columnVisibility ?? initialState?.columnVisibility ?? {});
  const [expanded, setExpanded] = useState<ExpandedState>(state?.expanded ?? initialState?.expanded ?? {});
  const [globalFilter, setGlobalFilter] = useState<string>(state?.globalFilter ?? persisted.globalFilter ?? initialState?.globalFilter ?? "");
  const [grouping, setGrouping] = useState<GroupingState>(state?.grouping ?? persisted.grouping ?? initialState?.grouping ?? []);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(state?.rowSelection ?? persisted.rowSelection ?? initialState?.rowSelection ?? {});
  const [sorting, setSorting] = useState<SortingState>(state?.sorting ?? persisted.sorting ?? initialState?.sorting ?? []);

  const tableState = {
    ...state,
    columnFilters: state?.columnFilters ?? columnFilters,
    columnOrder: state?.columnOrder ?? columnOrder,
    columnPinning: state?.columnPinning ?? columnPinning,
    columnSizing: state?.columnSizing ?? columnSizing,
    columnVisibility: state?.columnVisibility ?? columnVisibility,
    expanded: state?.expanded ?? expanded,
    globalFilter: state?.globalFilter ?? globalFilter,
    grouping: state?.grouping ?? grouping,
    rowSelection: state?.rowSelection ?? rowSelection,
    sorting: state?.sorting ?? sorting,
  };

  useEffect(() => {
    if (!persistState || nativeState || typeof window === "undefined") return;
    const next: PersistedTableState = {
      columnFilters: tableState.columnFilters,
      columnOrder: tableState.columnOrder,
      columnPinning: tableState.columnPinning,
      columnSizing: tableState.columnSizing,
      columnVisibility: tableState.columnVisibility,
      globalFilter: tableState.globalFilter,
      grouping: tableState.grouping,
      rowSelection: tableState.rowSelection,
      sorting: tableState.sorting,
    };
    window.localStorage.setItem(storageKey(tableKey), JSON.stringify(next));
  }, [
    persistState,
    nativeState,
    tableKey,
    tableState.columnFilters,
    tableState.columnOrder,
    tableState.columnPinning,
    tableState.columnSizing,
    tableState.columnVisibility,
    tableState.globalFilter,
    tableState.grouping,
    tableState.rowSelection,
    tableState.sorting,
  ]);

  return useBaseAppTable(
    {
      ...options,
      key: tableKey,
      initialState,
      ...(nativeState ? {} : {
      state: tableState,
      onColumnFiltersChange: chainOnChange(setColumnFilters, onColumnFiltersChange),
      onColumnOrderChange: chainOnChange(setColumnOrder, onColumnOrderChange),
      onColumnPinningChange: chainOnChange(setColumnPinning, onColumnPinningChange),
      onColumnSizingChange: chainOnChange(setColumnSizing, onColumnSizingChange),
      onColumnVisibilityChange: chainOnChange(setColumnVisibility, onColumnVisibilityChange),
      // Controlled expansion must be resolved against the owner's state. Resolving
      // it against the local copy drops descendant changes made by the owner, and
      // calling the owner inside a state updater replays it under Strict Mode.
      onExpandedChange: state?.expanded !== undefined && onExpandedChange
        ? (updater) => queueMicrotask(() => onExpandedChange(updater))
        : chainOnChange(setExpanded, onExpandedChange),
      onGlobalFilterChange: chainOnChange(setGlobalFilter, onGlobalFilterChange),
      onGroupingChange: chainOnChange(setGrouping, onGroupingChange),
      onRowSelectionChange: chainOnChange(setRowSelection, onRowSelectionChange),
      onSortingChange: chainOnChange(setSorting, onSortingChange),
      }),
    },
    (nextState) => ({
      columnFilters: nextState.columnFilters,
      columnOrder: nextState.columnOrder,
      columnPinning: nextState.columnPinning,
      columnSizing: nextState.columnSizing,
      columnVisibility: nextState.columnVisibility,
      expanded: nextState.expanded,
      globalFilter: nextState.globalFilter,
      grouping: nextState.grouping,
      rowSelection: nextState.rowSelection,
      sorting: nextState.sorting,
    }),
  );
}
